/**
 * Locked choices — one look for "you can't pick this", wherever it appears.
 *
 * Shop staff must see at a glance that a choice is closed to them and why,
 * on a phone as well as a desk (no hover tooltips). Every locked choice uses
 * the same amber lock, the same pill and the same tinted band:
 *
 *   - LockedMark    the lock that stands where the checkbox would be. Tapping it
 *                   (when `onPress` is given) opens the reason.
 *   - LockedBadge   the pill beside the choice's name ("Can't give", "Staff only").
 *   - LockedReason  the reason line under the name, at readable size.
 *   - LockedCallout the reason opened by a tap: focused, so it is read out.
 *   - LockedNotice  the one-line explanation above a list that has locked choices.
 *   - lockedBand    the background of a locked row or cell.
 *
 * Calm amber (the warning tokens), never alarm red, never just "faded".
 * Used by the role editors (permissions you can't give); role pickers that
 * hide roles you can't assign should reuse the same pieces.
 */
import React, { useEffect, useRef } from 'react';
import { Lock, X } from 'lucide-react';
import { textRole, tokenVars } from '../../theme';

/** Hatched amber band for a locked cell (no text on it). */
export const lockedHatch = `repeating-linear-gradient(135deg, ${tokenVars.warningSubtle} 0 6px, transparent 6px 12px)`;

/**
 * Background of a locked row or cell. `ground` is the opaque colour under the
 * tint (sticky cells must cover what scrolls beneath them); `hatched` stripes
 * it, for cells that hold only the lock.
 */
export function lockedBand(ground: string = tokenVars.surface, hatched = false): React.CSSProperties {
  return {
    backgroundColor: ground,
    backgroundImage: hatched ? lockedHatch : `linear-gradient(${tokenVars.warningSubtle}, ${tokenVars.warningSubtle})`,
  };
}

export interface LockedMarkProps {
  /** Spoken name: what is locked and why, e.g. "Technician: Edit invoices. Can't give. You don't have…".
   * Empty: a decorative lock (hidden from screen readers). */
  label: string;
  /** Tap handler (show the reason). Without it the mark is a picture, not a button. */
  onPress?: () => void;
  /** Whether the reason it opens is showing (aria-expanded). */
  expanded?: boolean;
  /** id of the reason it opens (aria-controls). */
  controls?: string;
  /** Box size in px; matches the checkbox it replaces. */
  size?: number;
  testId?: string;
  style?: React.CSSProperties;
}

/** The amber lock that stands in for a checkbox you can't tick. */
export const LockedMark = React.forwardRef<HTMLButtonElement, LockedMarkProps>(function LockedMark(
  { label, onPress, expanded, controls, size = 22, testId, style },
  ref
) {
  const look: React.CSSProperties = {
    width: size,
    height: size,
    minWidth: size,
    borderRadius: 6,
    border: `1.5px solid ${tokenVars.warning}`,
    background: tokenVars.warningSubtle,
    color: tokenVars.warningText,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    flexShrink: 0,
    boxSizing: 'border-box',
    ...style,
  };
  const icon = <Lock size={Math.round(size * 0.6)} strokeWidth={2.5} aria-hidden />;
  if (!onPress) {
    return (
      <span {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })} data-testid={testId} style={look}>
        {icon}
      </span>
    );
  }
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-expanded={expanded}
      aria-controls={controls}
      data-testid={testId}
      onClick={(e) => {
        e.stopPropagation();
        onPress();
      }}
      style={{ ...look, cursor: 'pointer' }}
    >
      {icon}
    </button>
  );
});

export interface LockedBadgeProps {
  /** Pill text, short: "Can't give", "Staff only", "3 can't give". */
  text: string;
  testId?: string;
  style?: React.CSSProperties;
}

/** The amber pill beside a locked choice's name. */
export const LockedBadge: React.FC<LockedBadgeProps> = ({ text, testId, style }) => (
  <span
    data-testid={testId}
    style={{
      ...textRole('pill', tokenVars.warningText),
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '1px 8px',
      borderRadius: 999,
      border: `1px solid ${tokenVars.warning}`,
      // Opaque, so the pill reads the same on any band it sits on.
      backgroundColor: tokenVars.surface,
      backgroundImage: `linear-gradient(${tokenVars.warningSubtle}, ${tokenVars.warningSubtle})`,
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      flexShrink: 0,
      ...style,
    }}
  >
    <Lock size={11} strokeWidth={2.5} aria-hidden />
    {text}
  </span>
);

export interface LockedReasonProps {
  children: React.ReactNode;
  id?: string;
  testId?: string;
  style?: React.CSSProperties;
}

/** The reason under a locked choice's name: readable size and contrast. */
export const LockedReason: React.FC<LockedReasonProps> = ({ children, id, testId, style }) => (
  <span id={id} data-testid={testId} style={{ ...textRole('meta', tokenVars.text.secondary), display: 'block', ...style }}>
    {children}
  </span>
);

export interface LockedCalloutProps {
  /** What was tapped, e.g. "Technician: Edit invoices". */
  title?: string;
  /** Why it is locked. */
  children: React.ReactNode;
  onClose: () => void;
  /** Where focus goes back to when ✕ or Escape closes it (the lock that opened it).
   * Defaults to whatever had focus when it opened. */
  returnFocus?: () => HTMLElement | null | undefined;
  id?: string;
  testId?: string;
  style?: React.CSSProperties;
}

/**
 * The reason, opened by tapping a locked choice. It takes focus when it opens
 * so a screen reader reads it; Escape or ✕ closes it and puts focus back on
 * the lock. Render it only in answer to a tap (it takes focus on mount).
 */
export const LockedCallout: React.FC<LockedCalloutProps> = ({ title, children, onClose, returnFocus, id, testId, style }) => {
  const ref = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    ref.current?.focus({ preventScroll: true });
    ref.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, []);
  const close = () => {
    const back = returnFocus?.() || opener.current;
    onClose();
    if (back && back !== document.body) setTimeout(() => back.isConnected && back.focus({ preventScroll: true }), 0);
  };
  return (
    <div
      ref={ref}
      id={id}
      role="status"
      tabIndex={-1}
      data-testid={testId}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          // Closes the callout, not the dialog around it.
          e.preventDefault();
          e.stopPropagation();
          close();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      style={{
        ...textRole('body', tokenVars.text.primary),
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 6,
        border: `1px solid ${tokenVars.warning}`,
        borderLeftWidth: 4,
        backgroundColor: tokenVars.surface,
        backgroundImage: `linear-gradient(${tokenVars.warningSubtle}, ${tokenVars.warningSubtle})`,
        outline: 'none',
        whiteSpace: 'normal',
        textAlign: 'left',
        ...style,
      }}
    >
      <Lock size={16} strokeWidth={2.5} aria-hidden style={{ color: tokenVars.warningText, flexShrink: 0, marginTop: 2 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        {title && <strong style={{ display: 'block' }}>{title}</strong>}
        {children}
      </span>
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        style={{
          background: 'transparent',
          border: 'none',
          color: tokenVars.text.secondary,
          cursor: 'pointer',
          padding: 2,
          display: 'inline-flex',
          flexShrink: 0,
        }}
      >
        <X size={16} aria-hidden />
      </button>
    </div>
  );
};

export interface LockedNoticeProps {
  children: React.ReactNode;
  /** Trailing link or button, e.g. a Help link. */
  action?: React.ReactNode;
  testId?: string;
  style?: React.CSSProperties;
}

/** The note above a list with locked choices: same lock and amber as the choices. */
export const LockedNotice: React.FC<LockedNoticeProps> = ({ children, action, testId, style }) => (
  <div
    data-testid={testId}
    style={{
      ...textRole('body', tokenVars.text.primary),
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '10px 12px',
      borderRadius: 6,
      borderLeft: `4px solid ${tokenVars.warning}`,
      background: tokenVars.warningSubtle,
      overflowWrap: 'anywhere',
      ...style,
    }}
  >
    <LockedMark label="" size={22} style={{ marginTop: -1 }} />
    <span style={{ flex: 1, minWidth: 0 }}>
      {children}
      {action && <> {action}</>}
    </span>
  </div>
);
